import { useState } from 'react'
import { BarChart3, FileText } from 'lucide-react'
import Card from '../components/Card'

export default function TextAnalyzer() {
  const [text, setText] = useState('')

  const analyzeText = () => {
    if (!text.trim()) {
      return null
    }

    const words = text.trim().split(/\s+/).filter(word => word.length > 0)
    const characters = text.length
    const charactersNoSpaces = text.replace(/\s/g, '').length
    const sentences = text.split(/[.!?]+/).filter(s => s.trim().length > 0)
    const paragraphs = text.split(/\n\s*\n/).filter(p => p.trim().length > 0)
    const lines = text.split('\n').length

    // Word frequency
    const wordFreq = {}
    words.forEach(word => {
      const cleanWord = word.toLowerCase().replace(/[^\w]/g, '')
      if (cleanWord) {
        wordFreq[cleanWord] = (wordFreq[cleanWord] || 0) + 1
      }
    })
    const mostCommonWords = Object.entries(wordFreq)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)

    // Average word length
    const avgWordLength = words.length > 0
      ? words.reduce((sum, word) => sum + word.replace(/[^\w]/g, '').length, 0) / words.length
      : 0

    // Average sentence length
    const avgSentenceLength = sentences.length > 0 ? words.length / sentences.length : 0

    // Reading time (average 200 words per minute)
    const readingTime = Math.ceil(words.length / 200)

    // Character frequency
    const charFreq = {}
    text.toLowerCase().replace(/\s/g, '').split('').forEach(char => {
      if (/[a-z]/.test(char)) {
        charFreq[char] = (charFreq[char] || 0) + 1
      }
    })
    const mostCommonChars = Object.entries(charFreq)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)

    return {
      words: words.length,
      characters,
      charactersNoSpaces,
      sentences: sentences.length,
      paragraphs: paragraphs.length || 1,
      lines,
      avgWordLength: avgWordLength.toFixed(2),
      avgSentenceLength: avgSentenceLength.toFixed(2),
      readingTime,
      mostCommonWords,
      mostCommonChars
    }
  }

  const stats = analyzeText()

  return (
    <div className="space-y-6">
      <Card>
        <Card.Body>
          <div className="mb-6">
            <label className="block text-gray-700 font-semibold mb-3">Enter text to analyze:</label>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              className="input w-full min-h-[200px]"
              placeholder="Paste or type your text here..."
            />
          </div>
        </Card.Body>
      </Card>

      {stats ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <Card>
            <Card.Body>
              <div className="flex items-center mb-4">
                <FileText className="w-5 h-5 text-purple-600 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">Basic Statistics</h3>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Words:</span>
                  <span className="font-semibold text-gray-900">{stats.words}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Characters:</span>
                  <span className="font-semibold text-gray-900">{stats.characters}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Characters (no spaces):</span>
                  <span className="font-semibold text-gray-900">{stats.charactersNoSpaces}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Sentences:</span>
                  <span className="font-semibold text-gray-900">{stats.sentences}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Paragraphs:</span>
                  <span className="font-semibold text-gray-900">{stats.paragraphs}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Lines:</span>
                  <span className="font-semibold text-gray-900">{stats.lines}</span>
                </div>
              </div>
            </Card.Body>
          </Card>

          <Card>
            <Card.Body>
              <div className="flex items-center mb-4">
                <BarChart3 className="w-5 h-5 text-purple-600 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">Averages</h3>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Avg. Word Length:</span>
                  <span className="font-semibold text-gray-900">{stats.avgWordLength} chars</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Avg. Sentence Length:</span>
                  <span className="font-semibold text-gray-900">{stats.avgSentenceLength} words</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Reading Time:</span>
                  <span className="font-semibold text-gray-900">{stats.readingTime} min</span>
                </div>
              </div>
            </Card.Body>
          </Card>

          <Card>
            <Card.Body>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Most Common Words</h3>
              <div className="space-y-2">
                {stats.mostCommonWords.map(([word, count], index) => (
                  <div key={index} className="flex justify-between items-center">
                    <span className="text-gray-700">{word}</span>
                    <span className="text-purple-600 font-semibold">{count}</span>
                  </div>
                ))}
              </div>
            </Card.Body>
          </Card>
        </div>
      ) : (
        <Card>
          <Card.Body className="text-center py-12">
            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Enter text above to see analysis statistics</p>
          </Card.Body>
        </Card>
      )}
    </div>
  )
}

